"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, HardHat, Ruler, Building2, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: 1,
  title: "For Contractors",
  description: "Empower your business with financing as a powerful sales tool. We provide the bridge to platforms that offer your clients immediate budget flexibility, helping you close larger projects with confidence.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/contractor_tablet_site.png",
  icon: Building2
}, {
  id: 2,
  title: "For Homeowners",
  description: "Unlock the potential of your property with access to competitive financing rates. We connect you with trusted lenders to ensure your next renovation or repair is affordable and stress-free.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/happy_family_kitchen.png",
  icon: Hammer
}, {
  id: 3,
  title: "Financing Networks",
  description: "Leverage our extensive connections within the fintech landscape. We act as your specialized scout, navigating the complex world of lending to find the most favorable terms for your unique situation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/integration_dashboard.png",
  icon: Ruler
}, {
  id: 4,
  title: "Expert Connection",
  description: "Experience the security of a professional partnership. While we aren't the lender, our role as your strategic facilitator ensures you have the right technology and support at every stage.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/professional_handshake.png",
  icon: HardHat
}];
const serviceIncludes = [{
  serviceId: 1,
  id: "c1",
  title: "Financing as a Sales Tool",
  detail: "Integrate flexible payment options directly into your estimates to increase conversion rates and average project value."
}, {
  serviceId: 1,
  id: "c2",
  title: "Instant Contractor Portals",
  detail: "Gain access to streamlined dashboards where you can track applications and manage client financing in real-time."
}, {
  serviceId: 1,
  id: "c3",
  title: "Transparent Fee Structures",
  detail: "Clear, upfront breakdowns of how financing affects your bottom line with no hidden costs or surprises."
}, {
  serviceId: 2,
  id: "r1",
  title: "Access to Competitive Rates",
  detail: "We scan our network to connect you with platforms offering the lowest possible interest rates and favorable terms."
}, {
  serviceId: 2,
  id: "r2",
  title: "Soft Credit Check Prequalification",
  detail: "Explore your options without impacting your credit score, allowing for confident planning before the project begins."
}, {
  serviceId: 2,
  id: "r3",
  title: "Flexible Repayment Plans",
  detail: "Find solutions that fit your monthly budget, with terms ranging from short-term bridge loans to multi-year plans."
}, {
  serviceId: 3,
  id: "g1",
  title: "Network Integration",
  detail: "How it integrates: Our API-driven approach connects your project data directly to lending algorithms for faster processing."
}, {
  serviceId: 3,
  id: "g2",
  title: "Regulatory Tech Compliance",
  detail: "We only partner with platforms that maintain the highest standards of financial security and regulatory compliance."
}, {
  serviceId: 4,
  id: "s1",
  title: "Strategic Facilitation",
  detail: "How it integrates: We provide the technical handshake between your project needs and the capital required to execute them."
}, {
  serviceId: 4,
  id: "s2",
  title: "Secure Data Handling",
  detail: "Your information is protected by enterprise-grade encryption throughout the entire connection process."
}];
function ServiceRow({
  service,
  index,
  includes
}: {
  service: typeof services[0];
  index: number;
  includes: typeof serviceIncludes;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = service.icon;
  return <div data-section-id="124361" ref={ref} id={index === 0 ? "financing-partnerships" : undefined} data-index={index} className={cn("flex flex-col gap-10 lg:gap-16 items-center mb-24 last:mb-0 transition-all duration-1000 ease-out", index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="w-full lg:w-1/2 relative group">
        <div className="absolute inset-0 bg-primary/5 translate-x-4 translate-y-4 -z-10 transition-transform duration-500 group-hover:translate-x-6 group-hover:translate-y-6" />
        <div className="relative aspect-[4/3] overflow-hidden border border-border bg-card shadow-2xl rounded-2xl">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale-[10%] group-hover:grayscale-0" loading="lazy" />
          <div className="absolute top-4 left-4 bg-background/80 backdrop-blur-md p-3 border border-border shadow-md rounded-xl">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        </div>
      </div>

      {}
      <div className="w-full lg:w-1/2 flex flex-col justify-center">
        <div className="border-l-4 border-primary/20 pl-6 mb-8">
          <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 uppercase font-sans">
            {service.title}
          </h3>
          <p className="text-muted-foreground text-lg leading-relaxed font-sans">
            {service.description}
          </p>
        </div>

        <div className="mb-8">
          <h4 className="text-xs font-bold uppercase tracking-widest text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-[2px] bg-primary inline-block"></span>
            Technical Breakdown
          </h4>
          <Accordion type="single" collapsible className="w-full border-t border-border">
            {includes.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-border">
                <AccordionTrigger className="text-base font-semibold hover:text-primary transition-colors py-4">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed font-sans pb-4">
                  {item.detail}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div>
          <Button asChild size="lg" className="group rounded-full uppercase tracking-widest font-bold shadow-lg bg-primary hover:bg-primary/90 transition-all">
            <Link to="/OurSolutionsPage">
              Explore Solution
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function OurSpecializedSolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section id="financing-partnerships" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-1000", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-extrabold tracking-tighter uppercase text-foreground mb-6 font-sans">
            Financing <span className="text-primary italic">Partnerships</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl mx-auto">
            Experts, One Connection. We bridge the gap between complex project needs and high-performance financing platforms.
          </p>
        </div>

        {}
        <div className="flex flex-col">
          {services.map((service, index) => {
          const includes = serviceIncludes.filter(inc => inc.serviceId === service.id);
          return <ServiceRow key={service.id} service={service} index={index} includes={includes} />;
        })}
        </div>

      </div>
    </section>;
}